import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, startWith, map } from 'rxjs/operators';

@Component({
  selector: 'lum-ui-autocomplete-chip',
  templateUrl: './ui-autocomplete-chip.component.html',
  styleUrls: ['./ui-autocomplete-chip.component.scss'],
})
export class UiAutocompleteChipComponent implements OnInit, OnDestroy {
  @Input()
  placeholder :string = '';

  @Input()
  displayProperty :string = 'name'; // This is the property that will be displayed in the dropdown

  @Input()
  loading :boolean = false;

  @Input()
  debounceTime :number = 0;

  @Input()
  options :any[] | null = null;

  @Input()
  bindValue :string | null = null; // Return the property value of the selected option instead of the whole object

  @Input()
  selectedOptions :any[] = [];

  @Input()
  inlineChips :boolean = false;

  @Input()
  formArray :UntypedFormArray;

  @Output()
  inputChangeEvent = new EventEmitter<any>();

  @Output()
  optionSelectedEvent = new EventEmitter<any>();

  @Output()
  optionRemove = new EventEmitter<any>();

  @ViewChild('optionInput')
  optionInput :ElementRef<HTMLInputElement>;

  separatorKeysCodes :number[] = [ENTER, COMMA];

  optionControl = new UntypedFormControl();

  inputUpdated$ = new Subject<string>();

  private onDestroy$ = new Subject();

  constructor() {}

  ngOnInit() :void {
    this.listenToOptionControlChanges();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onAdd(option :any) :void {
    this.optionInput.nativeElement.value = '';
    this.optionControl.setValue(this.options);

    this.optionSelectedEvent.emit(option);

    if (this.formArray) {
      if (this.bindValue) {
        this.formArray.push(new UntypedFormControl(option[this.bindValue]));
      } else {
        this.formArray.push(new UntypedFormControl(option));
      }
    }
  }

  onRemove(option :any) :void {
    this.optionRemove.emit(option);
    if (this.formArray) {
      const index = this.formArray.value.findIndex(
        (item :any) => item.id === option.id,
      );
      this.formArray.removeAt(index);
    }
  }

  trackByFn(option :any) :string {
    return option[this.displayProperty];
  }

  private listenToOptionControlChanges() :void {
    this.optionControl.valueChanges
      .pipe(
        startWith(null),
        debounceTime(this.debounceTime),
        map((value :string) => {
          this.inputChangeEvent.emit(value);
        }),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  get uniqueFilteredOptions() :any[] {
    return this.options
      ? this.options.filter(
          (option :any) =>
            !this.selectedOptions.find(
              (selectedOption :any) =>
                selectedOption[this.displayProperty] ===
                option[this.displayProperty],
            ),
        )
      : [];
  }
}
